import {ChangeDetectionStrategy, ChangeDetectorRef, Component, ViewChild} from '@angular/core';
import {DxPopupComponent} from 'devextreme-angular/ui/popup';
import {ToastrService} from 'ngx-toastr';
import {Subject} from 'rxjs';
import {filter} from 'rxjs/internal/operators/filter';
import {takeUntil} from 'rxjs/internal/operators/takeUntil';
import {SessionService} from '../authentication/session-service.service';
import {MessageBusService} from '../message-bus.service';
import {PanelBaseComponent} from '../panels/panel-base.component';
import {AccountDto} from '../shell-communication/dtos/account-dto.class';
import {TerminalDto} from '../shell-communication/dtos/terminal-dto.class';
import {ShellClientService} from '../shell-communication/shell-client.service';
import {
    ComboCreatedDto,
    ComboDto,
    ComboGroupCreatedDto,
    ComboGroupDto,
    GetAvailableBucketsReply,
    GroupDeletedDto,
    PortfolioCreatedDto,
    PortfolioDto
} from '../shell-communication/shell-dto-protocol';
import {
    GetAvailableBuckets,
    MultiLegTradeDto,
    PlaceMultiLegTrades
} from '../shell-communication/shell-operations-protocol';
import {
    BucketHighlighted,
    BucketItemHighlighted,
    ComboHighlightedItem,
    ComboHighlightedUIMessage,
    MultiLegOrderDataUIMessage,
    SymbolHighlighted
} from '../ui-messages/ui-messages';
import {
    calculateLedgerPrice,
    checkClipboardPermissions,
    DetectMethodChanges,
    DetectSetterChanges,
    findHCF,
    getShortUUID,
    isFuturesTicker,
    isNullOrUndefined,
    isValidNumber,
    isVoid,
    makeFuturesTosDesktopLine
} from '../utils';
import {ActionType, IMultiTradePadComponent, OrderDestination, OrderLinkType,} from './multi-trade-pad.model';
import {OrderLeg} from './multi-leg-order/order-leg.class';
import {OrderConfirmationService} from '../manual-trading/order-confirmation-service.service';
import {MultiLegOrderComponent} from './multi-leg-order/multi-leg-order.component';
import {PortfolioItemType} from '../portfolios/portfolios.model';
import {UserSettingsService} from "../user-settings.service";
import {LocationService} from "../location.service";
import {
    OrdersVerificationDialogConfig
} from "../options-pricing-grid/orders-verification-dialog/orders-verification-dialog.config";
import {
    OrdersVerificationDialogComponent
} from "../options-pricing-grid/orders-verification-dialog/orders-verification-dialog.component";
import {makeOptionTickerDisplayNameVerbose, parseOptionTicker} from "../options-common/options.model";
import {TradingInstrumentDisplayNameService} from "../trading-instruments/trading-instrument-display-name.service";
import {MarketSide} from "../trading-model/market-side.enum";
import {OrdersRegroupDialogComponent} from "../orders-regroup-dialog/orders-regroup-dialog.component";
import {RegroupOrdersConfig} from "../orders-regroup-dialog/RegroupOrdersConfig";
import {OrderToRegroupWrapper} from "../orders-regroup-dialog/OrderToRegroupWrapper";
import {OrdersGroupToRegroup} from "../orders-regroup-dialog/OrdersGroupToRegroup";
import {RegroupedOrders} from "../orders-regroup-dialog/RegroupedOrders";
import {HedgeOrder} from "../hedging-grid/data-model/hedge-order";
import {DateTime} from "luxon";
import {ClipboardService} from "../clipboard.service";


//

interface PanelState {
    isLinkedToSymbol: boolean;
}

//

function removeBucketFromComponent(collection: Array<any>, id: string, itemId: string) {

    if (isNullOrUndefined(collection)) {
        return;
    }

    if (isNullOrUndefined(id)) {
        return;
    }

    const ix = collection.findIndex(c => c[id] === itemId);

    if (ix >= 0) {
        collection.splice(ix, 1);
    }
}


function addBucketToComponent(collection: Array<any>, id: string, item: any) {

    if (isNullOrUndefined(collection)) {
        return;
    }

    if (isNullOrUndefined(id)) {
        return;
    }

    const ix = collection.findIndex(c => c[id] === item[id]);

    if (ix >= 0) {
        collection.splice(ix, 1);
    }

    collection.push(item);
}

//

@Component({
    selector: 'ets-multi-trade-pad',
    templateUrl: 'multi-trade-pad.component.html',
    styleUrls: ['./multi-trade-pad.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MultiTradePadComponent extends PanelBaseComponent implements IMultiTradePadComponent {

    constructor(
        protected readonly _changeDetector: ChangeDetectorRef,
        protected readonly _userSettingsService: UserSettingsService,
        protected readonly _messageBus: MessageBusService,
        private readonly _toastr: ToastrService,
        private readonly _shellClient: ShellClientService,
        private readonly _sessionService: SessionService,
        private readonly _orderConfirmationService: OrderConfirmationService,
        private readonly _locationService: LocationService,
        private readonly _clipboardService: ClipboardService,
        private readonly _displayNameService: TradingInstrumentDisplayNameService
    ) {
        super(_changeDetector, _userSettingsService, _messageBus);
    }

    //

    private _unsubscriber: Subject<any> = new Subject<any>();

    //

    get changeDetector() {
        return this._changeDetector;
    }

    //

    @ViewChild(DxPopupComponent) readonly confirmationDialog: DxPopupComponent;
    @ViewChild('mainOrder', {static: false}) mainOrderComp: MultiLegOrderComponent;
    @ViewChild('linkedOrderA', {static: false}) linkedOrderAComp: MultiLegOrderComponent;
    @ViewChild('linkedOrderB', {static: false}) linkedOrderBComp: MultiLegOrderComponent;

    //

    linkedOrderAType: OrderLinkType;

    linkedOrderBType: OrderLinkType;

    readonly linkedOrderTypeList: OrderLinkType[] = [
        'OTO',
        'OCO'
    ];

    //

    get higlightTargetOrder(): MultiLegOrderComponent {
        return this.mainOrderComp;
    }

    //

    private _isEmulationMode = false;
    get isEmulationMode(): boolean {
        return this._isEmulationMode;
    }

    @DetectSetterChanges()
    set isEmulationMode(val: boolean) {

        this._isEmulationMode = val;

        if (val) {
            this.linkedOrderAType = null;
            this.linkedOrderBType = null;
        }

    }

    //

    private _sendingOrder = false;
    get sendingOrder(): boolean {
        return this._sendingOrder;
    }

    @DetectSetterChanges()
    set sendingOrder(val: boolean) {
        this._sendingOrder = val;
    }

    //

    get canPlaceOrders(): boolean {
        return this.confirmationModel.orders.every(x => x.verified);
    }

    //

    availableTerminals: TerminalDto[];

    availableAccounts: AccountDto[];

    availablePortfolios: PortfolioDto[];

    availableCombos: ComboDto[];

    availableComboGroups: ComboGroupDto[];

    //

    @ViewChild(OrdersVerificationDialogComponent)
    orderVerificationDialog: OrdersVerificationDialogComponent;

    @ViewChild(OrdersRegroupDialogComponent)
    ordersRegroupDialog: OrdersRegroupDialogComponent;

    //

    confirmationModel: { orders: any[], dialogHeight: number, selectedDestination: number } = {
        orders: [],
        dialogHeight: 0,
        selectedDestination: 0
    };

    //

    portfolioDefaultQty: number = 1;

    async etsOnInit() {

        this.availableTerminals = this._sessionService.loginResult.availableTerminals.filter(
            terminal => !terminal.isProxy
        );

        this.availableAccounts = this._sessionService.loginResult.availableAccounts.slice();

        this.isLoading = true;

        try {

            const qry = new GetAvailableBuckets(false);
            const reply = await this._shellClient.processQuery<GetAvailableBucketsReply>(qry);

            this.availablePortfolios = reply.portfolios;
            this.availableCombos = reply.combos;
            this.availableComboGroups = reply.comboGroups;

        } finally {

            this.isLoading = false;

        }

        this.subscribeToMessages();

        this.restoreState();
    }

    //

    etsAfterViewInit() {
        console.assert(!isNullOrUndefined(this.mainOrderComp));
        console.assert(!isNullOrUndefined(this.linkedOrderAComp));
        console.assert(!isNullOrUndefined(this.linkedOrderBComp));
    }

    //

    @DetectMethodChanges()
    onDestinationRemoved(source: 'main' | 'link-a' | 'link-b', ix: number) {
        const components: MultiLegOrderComponent[] = [];

        if (source !== 'main') {
            components.push(this.mainOrderComp);
        }

        if (source !== 'link-a') {
            if (!isNullOrUndefined(this.linkedOrderAType)) {
                components.push(this.linkedOrderAComp);
            }
        }

        if (source !== 'link-b') {
            if (!isNullOrUndefined(this.linkedOrderBType)) {
                components.push(this.linkedOrderBComp);
            }
        }

        components.forEach(x => x.orderModel.removeDestination(ix, true));
    }

    //

    @DetectMethodChanges()
    onDestinationPressed(source: 'main' | 'link-a' | 'link-b', data: { ix: number, state: boolean }) {
        const components: MultiLegOrderComponent[] = [];

        if (source !== 'main') {
            components.push(this.mainOrderComp);
        }

        if (source !== 'link-a') {
            if (!isNullOrUndefined(this.linkedOrderAType)) {
                components.push(this.linkedOrderAComp);
            }
        }

        if (source !== 'link-b') {
            if (!isNullOrUndefined(this.linkedOrderBType)) {
                components.push(this.linkedOrderBComp);
            }
        }

        components.forEach(x => x.orderModel.pressDestination(data.ix, data.state, true));
    }

    //

    @DetectMethodChanges()
    onDestinationAdded(source: 'main' | 'link-a' | 'link-b', dest: OrderDestination) {
        const components: MultiLegOrderComponent[] = [];

        if (source !== 'main') {
            components.push(this.mainOrderComp);
        }

        if (source !== 'link-a') {
            if (!isNullOrUndefined(this.linkedOrderAType)) {
                components.push(this.linkedOrderAComp);
            }
        }

        if (source !== 'link-b') {
            if (!isNullOrUndefined(this.linkedOrderBType)) {
                components.push(this.linkedOrderBComp);
            }
        }

        components.forEach(comp => comp.orderModel.addDestination(dest, true));
    }

    //

    private subscribeToMessages() {

        this._messageBus.of<BucketHighlighted>('BucketHighlighted')
            .pipe(
                takeUntil(this._unsubscriber),
                filter(msg => msg.scopeId === this.layoutTabId)
            )
            .subscribe(msg => this.onBucketHighlighted(msg.payload));

        this._messageBus
            .of<BucketItemHighlighted>('BucketItemHighlighted')
            .pipe(
                takeUntil(this._unsubscriber),
                filter(msg => msg.scopeId === this.layoutTabId),
                filter(msg => msg.payload.item.itemType !== PortfolioItemType.Strategy)
            )
            .subscribe(msg => this.onBucketItemHighlighted(msg.payload));

        this._messageBus
            .of<PortfolioCreatedDto>('PortfolioCreatedDto')
            .pipe(
                takeUntil(this._unsubscriber)
            )
            .subscribe(msg => this.onPortfolioCreatedMessage(msg.payload));

        this._messageBus
            .of<ComboCreatedDto>('ComboCreatedDto')
            .pipe(takeUntil(this._unsubscriber))
            .subscribe((msg) => this.onComboCreatedMessage(msg.payload));

        this._messageBus
            .of<ComboGroupCreatedDto>('ComboGroupCreatedDto')
            .pipe(takeUntil(this._unsubscriber))
            .subscribe((msg) => this.onComboGroupCreatedMessage(msg.payload));

        this._messageBus
            .of<GroupDeletedDto>('GroupDeletedDto')
            .pipe(
                takeUntil(this._unsubscriber)
            )
            .subscribe(dto => this.onGroupDeletedMessage(dto.payload));

        this._messageBus
            .of<SymbolHighlighted>('SymbolHighlighted')
            .pipe(
                takeUntil(this._unsubscriber),
                filter(msg => msg.scopeId === this.layoutTabId),
                filter(_ => this.isLinkedToSymbol),
            )
            .subscribe(msg => this.onSymbolHighlightedMessage(msg.payload));
    }

    //

    private onSymbolHighlightedMessage(msg: SymbolHighlighted): void {
        if (!this.isLinkedToSymbol) {
            return;
        }

        this.higlightTargetOrder.processSymbolHighlightedMessage(msg);
    }

    //

    private onGroupDeletedMessage(dto: GroupDeletedDto): void {

        let collection: any[];
        let id: string;
        let itemId: string;

        switch (dto.groupType) {
            case 'combo': {
                collection = this.availableCombos;
                id = 'comboId';
                itemId = dto.comboId;
            }
                break;

            case 'group': {
                collection = this.availableComboGroups;
                id = 'comboGroupId';
                itemId = dto.comboGroupId;
            }
                break;

            case 'portfolio': {
                collection = this.availablePortfolios;
                id = 'portfolioId';
                itemId = dto.portfolioId;
            }
                break;

            default:
                return;
        }

        removeBucketFromComponent(
            collection,
            id,
            itemId
        );
    }

    //

    private onComboGroupCreatedMessage(msg: ComboGroupCreatedDto): void {
        addBucketToComponent(
            this.availableComboGroups,
            'comboGroupId',
            msg.comboGroup
        );
    }

    //

    private onComboCreatedMessage(msg: ComboCreatedDto): void {
        addBucketToComponent(
            this.availableCombos,
            'comboId',
            msg.combo
        );
    }

    //

    private onPortfolioCreatedMessage(dto: PortfolioCreatedDto): void {
        addBucketToComponent(
            this.availablePortfolios,
            'portfolioId',
            dto.portfolio
        );
    }

    //

    private onBucketItemHighlighted(msg: BucketItemHighlighted): void {
        if (!this.higlightTargetOrder.isLinkedMode) {
            return;
        }

        this.higlightTargetOrder.processBucketItemHighlightedMessage(msg);
    }

    //

    private onBucketHighlighted(msg: BucketHighlighted): void {
        if (!this.higlightTargetOrder.isLinkedMode) {
            return;
        }

        this.higlightTargetOrder.processBucketHighlightedMessage(msg);
    }

    //

    etsOnDestroy(): void {

        if (this._unsubscriber) {
            this._unsubscriber.next();
            this._unsubscriber.complete();
        }

    }


    //#endregion

    toggleTooltip(leg: OrderLeg, property: string) {

        const tooltipProp = `tooltip_${property}`;

        leg[tooltipProp] = !leg[tooltipProp];

        if (!leg[tooltipProp]) {

            this._changeDetector.detectChanges();

        } else {

            const tooltipState = leg[tooltipProp];

            setTimeout(() => {
                if (tooltipState === leg[tooltipProp]) {
                    this._changeDetector.detectChanges();
                }
            }, 700);
        }
    }

    //

    @DetectMethodChanges()
    onLinkedOrderATypeSelected(ev: { value: OrderLinkType, previousValue: OrderLinkType }) {

        this.linkedOrderAType = ev.value;

        if (isNullOrUndefined(this.linkedOrderAComp)) {
            return;
        }

        if (isNullOrUndefined(ev.previousValue)) {
            this.linkedOrderAComp.recreateOrderModel(this.mainOrderComp.orderModel);
            return;
        }
    }

    //

    @DetectMethodChanges()
    onLinkedOrderBTypeSelected(ev: { value: OrderLinkType, previousValue: OrderLinkType }) {

        this.linkedOrderBType = ev.value;

        if (isNullOrUndefined(this.linkedOrderAComp)) {
            return;
        }

        if (isNullOrUndefined(this.linkedOrderBComp)) {
            return;
        }

        if (isNullOrUndefined(ev.previousValue)) {
            this.linkedOrderBComp.recreateOrderModel(this.linkedOrderAComp.orderModel);
            return;
        }
    }

    //

    @DetectMethodChanges()
    async verifyOrders() {
        this.mainOrderComp.onExpirationStyleChanged();

        if (!this._locationService.isTradingMasteryHost) {
            const mainOrderErrors = this.mainOrderComp.validateOrder();

            if (mainOrderErrors.length > 0) {

                mainOrderErrors.forEach(error => {
                    this._toastr.error(error, 'Main Order');
                });

                return;
            }

            if (!isNullOrUndefined(this.linkedOrderAType)) {

                if (isNullOrUndefined(this.linkedOrderAComp)) {

                    this._toastr.error('Order not available', 'Linked #1');

                    return;
                }

                this.linkedOrderAComp.onExpirationStyleChanged();
                const linkedOrderAErrors = this.linkedOrderAComp.validateOrder();

                if (linkedOrderAErrors.length > 0) {

                    linkedOrderAErrors.forEach(error => {
                        this._toastr.error(error, 'Linked #1');
                    });

                    return;
                }

                if (!isNullOrUndefined(this.linkedOrderBType)) {

                    if (isNullOrUndefined(this.linkedOrderBComp)) {

                        this._toastr.error('Order not available', 'Linked #2');

                        return;
                    }

                    this.linkedOrderBComp.onExpirationStyleChanged();

                    const linkedOrderBErrors = this.linkedOrderBComp.validateOrder();

                    if (linkedOrderBErrors.length > 0) {

                        linkedOrderBErrors.forEach(error => {
                            this._toastr.error(error, 'Linked #2');
                        });

                        return;
                    }
                }
            }

            this.showConfirmationDialog();

        } else {
            const mainOrderErrors = this.mainOrderComp.validateOrderForTradingMastery();

            if (mainOrderErrors.length > 0) {

                mainOrderErrors.forEach(error => {
                    this._toastr.error(error, 'Main Order');
                });

                return;
            }

            await this.showTradingMasteryConfirmationDialog();
        }
    }

    //

    @DetectMethodChanges({isAsync: true})
    async sendOrders(): Promise<void> {

        const mainOrders = this.mainOrderComp.getOrderDto();

        const mlTrades: MultiLegTradeDto[] = mainOrders.map((dto, ix) => {

            const mlTrade = new MultiLegTradeDto(dto);

            mlTrade.mainOrderAttachment = this.mainOrderComp.attachment;
            mlTrade.futureOrderSettings = this.mainOrderComp.futureOrderSettings;
            mlTrade.convertToMarketSettings = this.mainOrderComp.convertToMarketSettings;
            mlTrade.cashFlowSpec = this.mainOrderComp.cashFlowSpec;

            if (this.linkedOrderAType) {

                try {

                    const linkedAOrders = this.linkedOrderAComp.getOrderDto();
                    mlTrade.linkedOrderA = linkedAOrders[ix];
                    mlTrade.linkAType = this.linkedOrderAType;
                    mlTrade.linkedOrderAAttachment = this.linkedOrderAComp.attachment;

                } catch {
                    this._toastr.error('Failed to created Linked Order #1', 'Linked #1');

                    return;
                }


                if (this.linkedOrderBType) {
                    try {

                        const linkedBOrders = this.linkedOrderBComp.getOrderDto();
                        mlTrade.linkedOrderB = linkedBOrders[ix];
                        mlTrade.linkBType = this.linkedOrderBType;
                        mlTrade.linkedOrderBAttachment = this.linkedOrderBComp.attachment;

                    } catch {

                        this._toastr.error('Failed to created Linked Order #2', 'Linked #2');
                        return;
                    }

                }
            }

            return mlTrade;
        });

        const cmd = new PlaceMultiLegTrades(mlTrades);

        cmd.guiToken = getShortUUID();

        this.sendingOrder = true;

        try {

            this.isLoading = true;

            const ignoreConfirmation = true;

            if (ignoreConfirmation) {

                await this._shellClient.processCommand(cmd);

            } else {

                await this._shellClient.processCommand(cmd);
                await this._orderConfirmationService.registerToken(cmd.guiToken);

            }

        } catch (e) {

            console.error(e);
            this._toastr.error('"Place Multi-Leg Order" operation completed with errors', 'Multi-Leg Trade');

        } finally {

            this.isLoading = false;
            this.confirmationDialog.visible = false;
            this.sendingOrder = false;

            if (this.mainOrderComp) {
                this.mainOrderComp.resetAttachment();
                this.mainOrderComp.resetFutureOrderSettings();
                this.mainOrderComp.resetCashFlowSpec();
            }

            if (this.linkedOrderAComp) {
                this.linkedOrderAComp.resetAttachment();
                this.linkedOrderAComp.resetCashFlowSpec();
            }

            if (this.linkedOrderBComp) {
                this.linkedOrderBComp.resetAttachment();
                this.linkedOrderBComp.resetCashFlowSpec();
            }

        }
    }

    //

    @DetectMethodChanges()
    selectVerifyDestination(ix: number) {
        this.confirmationModel.orders[ix].verified = true;
        this.confirmationModel.selectedDestination = ix;
    }

    //

    private showConfirmationDialog() {
        let dialogHeight = 260; // just a empirical constant


        const mainOrderModels = this.mainOrderComp.getVerificationModels();
        dialogHeight += mainOrderModels[0].legs.length * 19;

        let linkedOrderAModels = [];
        if (this.linkedOrderAType) {
            linkedOrderAModels = this.linkedOrderAComp.getVerificationModels();
            dialogHeight += linkedOrderAModels[0].legs.length * 19 + 120;
            linkedOrderAModels.forEach(x => x.linkType = this.linkedOrderAType);
        }

        let linkedOrderBModels = [];
        if (this.linkedOrderBType) {
            linkedOrderBModels = this.linkedOrderBComp.getVerificationModels();
            dialogHeight += linkedOrderBModels[0].legs.length * 19 + 120;
            linkedOrderBModels.forEach(x => x.linkType = this.linkedOrderBType);
        }

        const destinations = mainOrderModels.map((mom, ix) => {
            const res: any = {
                mainOrder: mom
            };

            if (linkedOrderAModels.length >= ix) {
                res.linkedOrderA = linkedOrderAModels[ix];
            }

            if (linkedOrderBModels.length >= ix) {
                res.linkedOrderB = linkedOrderBModels[ix];
            }

            return res;
        });

        destinations[0].verified = true;

        this.confirmationModel.orders = destinations;
        this.confirmationModel.selectedDestination = 0;
        this.confirmationModel.dialogHeight = dialogHeight;

        this.confirmationDialog.visible = true;
    }

    //

    protected getState(): PanelState {

        const state: PanelState = {
            isLinkedToSymbol: this.isLinkedToSymbol
        };

        return state;
    }

    //

    protected setState(state: PanelState): void {
        this.isLinkedToSymbol = state.isLinkedToSymbol;
    }

    //
    @DetectMethodChanges({isAsync: true})
    async onLinkedOrdersPasted(msg: MultiLegOrderDataUIMessage, component: MultiLegOrderComponent): Promise<void> {

        const self = this;

        if (isVoid(msg.main)) {
            return;
        }

        if (isVoid(this.mainOrderComp)) {
            return;
        }

        self.isLoading = true;

        const orders = [msg.main, msg.linked, msg.secondLinked, msg.thirdLinked];

        if (component === this.mainOrderComp) {
            setTimeout(() => this.copyToMainOrder(msg, orders), 250);
        }
        else if (component === this.linkedOrderAComp) {
            setTimeout(() => this.copyToLinkedOrderA(msg, orders), 250);
        }
        else if (component === this.linkedOrderBComp) {
            setTimeout(() => this.copyToLinkedOrderB(msg, orders));
        }
        else {
            self.isLoading = false;
        }
    }

    private copyToMainOrder(msg: MultiLegOrderDataUIMessage, orders: ComboHighlightedUIMessage[]) {
        if (orders.length == 0) {
            return;
        }

        let hadError = false;

        this.mainOrderComp
            .processComboHighlightedMessage(orders[0])
            .catch(e => hadError = true)
            .finally(() => {
                if (msg.linked && msg.linked.items.length > 0) {
                    if (msg.linkType) {
                        this.linkedOrderAType = msg.linkType;
                    }
                    orders.splice(0, 1);
                    setTimeout(() => this.copyToLinkedOrderA(msg, orders), 250);
                } else {
                    this.isLoading = false;
                }
            });
    }

    private copyToLinkedOrderA(msg: MultiLegOrderDataUIMessage, orders: ComboHighlightedUIMessage[]) {
        if (orders.length === 0) {
            return;
        }

        let hadError = false;

        this.linkedOrderAComp
            .processComboHighlightedMessage(orders[0])
            .catch(e => hadError = true )
            .finally(() => {
                if (msg.secondLinked && msg.secondLinked.items.length > 0) {
                    this.linkedOrderBType = msg.linkType;
                    orders.splice(0,1);
                    setTimeout(() => this.copyToLinkedOrderB(msg, orders), 250);
                } else {
                    this.isLoading = false;
                }
            });
    }

    private copyToLinkedOrderB(msg: MultiLegOrderDataUIMessage, orders: ComboHighlightedUIMessage[]) {

        if (orders.length === 0) {
            return;
        }

        this.linkedOrderBComp
            .processComboHighlightedMessage(orders[0])
            .catch(e => console.error(e))
            .finally(() => {
                setTimeout(() => {
                    this.isLoading = false
                }, 250);
            });
    }

    private showTradingMasteryConfirmationDialog() {

        const configs: OrdersVerificationDialogConfig[] = [];

        if (this.mainOrderComp.orderHasLegs) {
            const cfg = this.makeVerificationConfigForTradingMastery(this.mainOrderComp);
            configs.push(cfg);
        }

        if (this.linkedOrderAComp.orderHasLegs && !isVoid(this.linkedOrderAType)) {
            const cfg = this.makeVerificationConfigForTradingMastery(this.linkedOrderAComp);
            configs.push(cfg);
        }

        if (this.linkedOrderBComp.orderHasLegs && !isVoid(this.linkedOrderBType)) {
            const cfg = this.makeVerificationConfigForTradingMastery(this.linkedOrderBComp);
            configs.push(cfg);
        }

        return this.orderVerificationDialog.show(configs, 'mlpad');
    }

    private makeVerificationConfigForTradingMastery(comp: MultiLegOrderComponent): OrdersVerificationDialogConfig {
        const comboItems = this.makeComboItemsForTradingMastery(comp);
        const config: OrdersVerificationDialogConfig = {
            orderLegs: comboItems.sort((a, b) => b.ticker.localeCompare(a.ticker)),
            orderPrice: undefined,
            defaultQty: this.portfolioDefaultQty,
            ledgerPrice: () => calculateLedgerPrice(comboItems, this.portfolioDefaultQty)
        };

        return config;
    }

    async showOrdersRegroupDialog(orderCmp: MultiLegOrderComponent) {

        const wrappers = orderCmp.orderModel.legs.map(leg => {
            const mlPadLeg = leg.asMlPadLeg();
            mlPadLeg.qty *= orderCmp.orderModel.destinations[0].qty;
            const wrpr = new OrderToRegroupWrapper(mlPadLeg);
            return wrpr;
        });

        const calls = wrappers.filter(x => x.type === 'Call');
        const puts = wrappers.filter(x => x.type === 'Put');

        const callWrappers = new OrdersGroupToRegroup(orderCmp.orderRole as any, calls);
        const putWrappers = new OrdersGroupToRegroup(orderCmp.orderRole as any, puts);

        let orders: OrdersGroupToRegroup;
        let orders2: OrdersGroupToRegroup;

        if (calls.length > 0 && puts.length > 0) {
            orders = callWrappers;
            orders2 = putWrappers;
        } else {
            orders = calls.length > 0 ? callWrappers : putWrappers;
        }

        const config: RegroupOrdersConfig = {
            orders: [orders],
            orders2: !isVoid(orders2) ? [orders2] : null,
            copyCallback: (args: RegroupedOrders[], dest?: string) => this.copyRegroupedOrdersToClipboard(args, dest),
            context: 'mlpad',
            defaultQty: orderCmp.orderModel.destinations[0].qty,
            reverseOrderValidator: _ => Promise.resolve()
        };

        try {
            await this.ordersRegroupDialog.show(config);
        } catch (e) {
            console.error(e);
        }
    }

    private makeComboItemsForTradingMastery(comp: MultiLegOrderComponent): ComboHighlightedItem[] {

        const legs = comp.orderModel.legs;

        const comboItems = legs.map(x => {

            const optionTicker = parseOptionTicker(x.ticker);

            const ulDisplayName = this._displayNameService.getDisplayNameForTicker(optionTicker.underlying);
            const displayName = makeOptionTickerDisplayNameVerbose(optionTicker, 'American', ulDisplayName);

            const optionType = optionTicker.type === 'Call'
                ? PortfolioItemType.Call
                : PortfolioItemType.Put;

            let side = MarketSide.Flat;
            if (x.action === ActionType.Buy) {
                side = MarketSide.Buy;
            } else if (x.action === ActionType.Sell) {
                side = MarketSide.Sell;
            }

            const qtyMultiplier = comp.orderModel.destinations[0].qty;

            const item: ComboHighlightedItem = {
                accountId: null,
                comboId: null,
                portfolioId: null,
                ticker: x.ticker,
                underlying: optionTicker.underlying,
                tickerDisplayName: displayName,
                netPosition: Math.abs(x.qty) * qtyMultiplier,
                side: side,
                itemType: optionType,
                strategyId: null
            };

            return item;
        });

        return comboItems;
    }

    private async copyRegroupedOrdersToClipboard(orders: RegroupedOrders[], destination: string): Promise<void> {

        try {
            switch (destination) {
                case "ETS":
                    this.copyOrdersToEts(orders);
                    break;
                case 'HG':
                    this.copyToHedgingGrid(orders);
                    break;
                case "ToS": {
                    const tosOk = await this.copyOrdersToToSDesktop(orders);
                    if (!tosOk) { return; }
                    break;
                }
                default:
                    this._toastr.warning('Unsupported destination');
                    return;
            }

            this._toastr.success('Order Copied To Clipboard!');
        } catch (error) {
            console.error(error);
            this._toastr.error('"Copy Orders To Clipboard" operation completed with errors');
        }


    }

    private makeComboItemsForCopy(orders: RegroupedOrders[]): ComboHighlightedItem[][] {

        const itemsGroups = orders.map(regroupItem => {

            const legs = regroupItem.orders;

            const comboItems = legs.map(x => {

                const optionTicker = parseOptionTicker(x.ticker);

                const ulDisplayName = this._displayNameService.getDisplayNameForTicker(optionTicker.underlying);
                const displayName = makeOptionTickerDisplayNameVerbose(optionTicker, 'American', ulDisplayName);

                const optionType = optionTicker.type === 'Call'
                    ? PortfolioItemType.Call
                    : PortfolioItemType.Put;

                let side = MarketSide.Flat;
                if (x.qty > 0) {
                    side = MarketSide.Buy;
                } else if (x.qty < 0) {
                    side = MarketSide.Sell;
                }

                const item: ComboHighlightedItem = {
                    accountId: null,
                    comboId: null,
                    portfolioId: null,
                    ticker: x.ticker,
                    underlying: optionTicker.underlying,
                    tickerDisplayName: displayName,
                    netPosition: Math.abs(x.qty),
                    side: side,
                    itemType: optionType,
                    strategyId: null
                };

                return item;
            });

            return comboItems;
        });

        return itemsGroups;
    }


    private async copyOrdersToToSDesktop(regrouped: RegroupedOrders[]) {

        let orders;

        if (regrouped) {
            orders = regrouped.map(x => {
                return {
                    orders: x.orders as HedgeOrder[],
                    limitPrice: x.limitPrice
                }
            });
        }

        const lines = orders
            .map(x => this.makeTosDesktopOrderLine(x));

        if (lines.some(l => isVoid(l))) {
            return false;
        }

        for (let i = lines.length - 1; i >= 0; i--) {
            if (i === 0) {
                break;
            }
            let line = lines[i];
            line += ' TRG BY';
            lines[i] = line;
        }

        const text = lines.join('\n');

        // @ts-ignore
        const result = await checkClipboardPermissions();

        if (result) {
            window.focus();
            await navigator.clipboard.writeText(text);
        } else {
            throw Error('Could not copy to clipboard. Please check browser permissions');
        }

        return true;
    }

    private makeTosDesktopOrderLine(args: { orders: HedgeOrder[], limitPrice?: number }): string {

        if (isVoid(args?.orders)) {
            return undefined;
        }

        const orders = args.orders;

        const futuresOrders = orders.filter(o => isFuturesTicker(o.asset));
        if (futuresOrders.length > 0 && futuresOrders.length !== orders.length) {
            this._toastr.error('Mixed futures and equity legs are not supported in a single TOS order');
            return undefined;
        }

        if (futuresOrders.length === orders.length) {
            return this.makeFuturesTosDesktopOrderLine(args);
        }

        return this.makeEquityTosDesktopOrderLine(args);
    }

    private makeEquityTosDesktopOrderLine(args: { orders: HedgeOrder[], limitPrice?: number }): string {

        const orders = args.orders;
        const limitPrice = args.limitPrice;

        const transformedLegGroups = orders.map(order => ({
            qty: order.qty,
            date: DateTime.fromFormat(order.expiration, 'yyyy-MM-dd').toFormat('dd MMM yy').toUpperCase(),
            strike: order.strike,
            type: order.type.toUpperCase(),
            price: order.price
        }));

        const underlying = orders[0].asset;

        const quantities = transformedLegGroups.map(x => x.qty).join('/');
        const dates = transformedLegGroups.map(x => x.date).join('/');
        const strikes = transformedLegGroups.map(x => x.strike).join('/');
        const types = transformedLegGroups.map(x => x.type).join('/');

        const comboPrice = transformedLegGroups.reduce((p, c) => p + c.price, 0) * -1;
        if (!isValidNumber(comboPrice)) {
            return undefined;
        }

        const px = isValidNumber(limitPrice) ? Math.abs(limitPrice) + '' : '';

        if (underlying === 'SPX') {
            return `BUY 1 ${quantities} CUSTOM ${underlying} 100 (Weeklys) ${dates} ${strikes} ${types} @${px} LMT GTC`;
        }
        return `BUY 1 ${quantities} CUSTOM ${underlying} 100 ${dates} ${strikes} ${types} @${px} LMT GTC`;
    }

    private makeFuturesTosDesktopOrderLine(args: { orders: HedgeOrder[], limitPrice?: number }): string {

        const orders = args.orders;
        const limitPrice = args.limitPrice;

        const uniqueExpirations = new Set(orders.map(o => o.expiration));
        if (uniqueExpirations.size > 1) {
            this._toastr.error('Multi-expiration combos are not supported yet for ES options in TOS Desktop format');
            return undefined;
        }

        const hcf = findHCF(orders.map(o => Math.abs(o.qty)));
        const totalQty = hcf;

        const transformedLegGroups = orders.map(order => ({
            qty: order.qty / hcf,
            date: DateTime.fromFormat(order.expiration, 'yyyy-MM-dd').toFormat('d MMM yy').toUpperCase(),
            strike: order.strike,
            type: order.type.toUpperCase(),
            price: order.price
        }));

        const comboPrice = transformedLegGroups.reduce((p, c) => p + c.price, 0) * -1;
        if (!isValidNumber(comboPrice)) {
            return undefined;
        }

        const underlying = orders[0].asset;
        const expDate = DateTime.fromFormat(orders[0].expiration, 'yyyy-MM-dd');
        const px = isValidNumber(limitPrice) ? Math.abs(limitPrice) + '' : '';

        return makeFuturesTosDesktopLine({
            expDate,
            underlying,
            strikes: transformedLegGroups.map(x => x.strike),
            types: transformedLegGroups.map(x => x.type),
            legQtys: transformedLegGroups.map(x => x.qty),
            totalQty,
            dates: transformedLegGroups.map(x => x.date),
            price: px
        });
    }


    private copyOrdersToEts(regrouped?: RegroupedOrders[]) {

        let orders: HedgeOrder[][] = [];

        if (regrouped) {
            orders = regrouped.map(x => x.orders as HedgeOrder[]);
        }

        const orderLegsGroups = orders.map(grp => {

            const mappedOrders = grp.map(o => {
                const marketSide = o.action.indexOf('Buy') >= 0 ? MarketSide.Buy : MarketSide.Sell;
                const item: ComboHighlightedItem = {
                    ticker: o.ticker,
                    underlying: o.asset,
                    side: marketSide,
                    itemType: null,
                    netPosition: o.qty
                } as any;

                return item;
            });

            const comboMsg: ComboHighlightedUIMessage = {
                items: mappedOrders,
                orderParams: {
                    orderQty: 1
                }
            }

            return comboMsg;
        });

        if (orderLegsGroups.length > 1) {

            const main = orderLegsGroups[0];
            const linked = orderLegsGroups[1];
            const secondLinked = orderLegsGroups[2];
            const thirdLinked = orderLegsGroups[3];

            const msg: MultiLegOrderDataUIMessage = {
                main,
                linked,
                secondLinked,
                thirdLinked,
                linkType: 'OTO',
                cashFlowSpec: {} as any,
            };

            this._clipboardService.put('multi-combo', msg);

        } else {

            const msg: ComboHighlightedUIMessage = {
                items: orderLegsGroups[0].items,
                orderParams: {
                    orderQty: 1
                }
            };

            this._clipboardService.put('combo', msg);
        }
    }

    private copyToHedgingGrid(regrouped?: RegroupedOrders[]) {
        const makeComboItemsForCopy = this.makeComboItemsForCopy(regrouped);

        const msgs = makeComboItemsForCopy.map(items => {
            const msg: ComboHighlightedUIMessage = {
                items: items,
            };
            return msg;
        });

        this._clipboardService.put('hg.pkg-cmprsn', msgs);
    }
}
